package at.apa.statistictracker.db.common

import at.apa.statistictracker.db.StatisticPayloadQueries
import at.apa.statistictracker.db.StatisticPayloadTable
import at.apa.statistictracker.db.StatisticTrackerDb
import at.apa.statistictracker.db.UserPropertyQueries
import at.apa.statistictracker.db.UserPropertyTable
import com.squareup.sqldelight.Query
import com.squareup.sqldelight.TransacterImpl
import com.squareup.sqldelight.`internal`.copyOnWriteList
import com.squareup.sqldelight.db.SqlCursor
import com.squareup.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Unit
import kotlin.collections.Collection
import kotlin.collections.MutableList
import kotlin.reflect.KClass
import kotlinx.serialization.json.JsonElement

internal val KClass<StatisticTrackerDb>.schema: SqlDriver.Schema
  get() = StatisticTrackerDbImpl.Schema

internal fun KClass<StatisticTrackerDb>.newInstance(driver: SqlDriver,
    statisticPayloadTableAdapter: StatisticPayloadTable.Adapter): StatisticTrackerDb =
    StatisticTrackerDbImpl(driver, statisticPayloadTableAdapter)

private class StatisticTrackerDbImpl(
  driver: SqlDriver,
  internal val statisticPayloadTableAdapter: StatisticPayloadTable.Adapter
) : TransacterImpl(driver), StatisticTrackerDb {
  public override val statisticPayloadQueries: StatisticPayloadQueriesImpl =
      StatisticPayloadQueriesImpl(this, driver)

  public override val userPropertyQueries: UserPropertyQueriesImpl = UserPropertyQueriesImpl(this,
      driver)

  public object Schema : SqlDriver.Schema {
    public override val version: Int
      get() = 1

    public override fun create(driver: SqlDriver): Unit {
      driver.execute(null, """
          |CREATE TABLE userPropertyTable (
          |    userPropertyTypeTag TEXT NOT NULL PRIMARY KEY,
          |    value TEXT NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE statisticPayloadTable (
          |    id INTEGER NOT NULL PRIMARY KEY AUTOINCREMENT,
          |    payload TEXT NOT NULL,
          |    timestampUnixMillis INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null,
          "CREATE INDEX statisticPayloadTable_timestamp_index ON statisticPayloadTable(timestampUnixMillis)",
          0)
    }

    public override fun migrate(
      driver: SqlDriver,
      oldVersion: Int,
      newVersion: Int
    ): Unit {
    }
  }
}

private class UserPropertyQueriesImpl(
  private val database: StatisticTrackerDbImpl,
  private val driver: SqlDriver
) : TransacterImpl(driver), UserPropertyQueries {
  internal val selectAll: MutableList<Query<*>> = copyOnWriteList()

  internal val lastInsertedId: MutableList<Query<*>> = copyOnWriteList()

  public override fun <T : Any> selectAll(mapper: (userPropertyTypeTag: String,
      value_: String) -> T): Query<T> = Query(-1499545174, selectAll, driver, "UserProperty.sq",
      "selectAll", "SELECT * FROM userPropertyTable") { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public override fun selectAll(): Query<UserPropertyTable> = selectAll { userPropertyTypeTag,
      value_ ->
    UserPropertyTable(
      userPropertyTypeTag,
      value_
    )
  }

  public override fun lastInsertedId(): Query<Long> = Query(-316847100, lastInsertedId, driver,
      "UserProperty.sq", "lastInsertedId", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  public override fun insertOrReplace(userPropertyTable: UserPropertyTable): Unit {
    driver.execute(-147039363,
        """INSERT OR REPLACE INTO userPropertyTable (userPropertyTypeTag, value) VALUES (?, ?)""",
        2) {
      bindString(1, userPropertyTable.userPropertyTypeTag)
      bindString(2, userPropertyTable.value_)
    }
    notifyQueries(-147039363, {database.userPropertyQueries.selectAll})
  }
}

private class StatisticPayloadQueriesImpl(
  private val database: StatisticTrackerDbImpl,
  private val driver: SqlDriver
) : TransacterImpl(driver), StatisticPayloadQueries {
  internal val selectAll: MutableList<Query<*>> = copyOnWriteList()

  internal val selectNOldest: MutableList<Query<*>> = copyOnWriteList()

  internal val lastInsertedId: MutableList<Query<*>> = copyOnWriteList()

  public override fun <T : Any> selectAll(mapper: (
    id: Long,
    payload: JsonElement,
    timestampUnixMillis: Long
  ) -> T): Query<T> = Query(-1852074612, selectAll, driver, "StatisticPayload.sq", "selectAll",
      "SELECT * FROM statisticPayloadTable") { cursor ->
    mapper(
      cursor.getLong(0)!!,
      database.statisticPayloadTableAdapter.payloadAdapter.decode(cursor.getString(1)!!),
      cursor.getLong(2)!!
    )
  }

  public override fun selectAll(): Query<StatisticPayloadTable> = selectAll { id, payload,
      timestampUnixMillis ->
    StatisticPayloadTable(
      id,
      payload,
      timestampUnixMillis
    )
  }

  public override fun <T : Any> selectNOldest(n: Long, mapper: (
    id: Long,
    payload: JsonElement,
    timestampUnixMillis: Long
  ) -> T): Query<T> = SelectNOldestQuery(n) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      database.statisticPayloadTableAdapter.payloadAdapter.decode(cursor.getString(1)!!),
      cursor.getLong(2)!!
    )
  }

  public override fun selectNOldest(n: Long): Query<StatisticPayloadTable> = selectNOldest(n) { id,
      payload, timestampUnixMillis ->
    StatisticPayloadTable(
      id,
      payload,
      timestampUnixMillis
    )
  }

  public override fun lastInsertedId(): Query<Long> = Query(265524578, lastInsertedId, driver,
      "StatisticPayload.sq", "lastInsertedId", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  public override fun deleteIds(ids: Collection<Long>): Unit {
    val idsIndexes = createArguments(count = ids.size)
    driver.execute(null, """
    |DELETE FROM statisticPayloadTable
    |WHERE id IN $idsIndexes
    """.trimMargin(), ids.size) {
      ids.forEachIndexed { index, ids_ ->
          bindLong(index + 1, ids_)
          }
    }
    notifyQueries(-494801004, {database.statisticPayloadQueries.selectAll +
        database.statisticPayloadQueries.selectNOldest})
  }

  public override fun insert(payload: JsonElement, timestampUnixMillis: Long): Unit {
    driver.execute(-914920046,
        """INSERT INTO statisticPayloadTable(payload, timestampUnixMillis) VALUES (?, ?)""", 2) {
      bindString(1, database.statisticPayloadTableAdapter.payloadAdapter.encode(payload))
      bindLong(2, timestampUnixMillis)
    }
    notifyQueries(-914920046, {database.statisticPayloadQueries.selectAll +
        database.statisticPayloadQueries.selectNOldest})
  }

  private inner class SelectNOldestQuery<out T : Any>(
    public val n: Long,
    mapper: (SqlCursor) -> T
  ) : Query<T>(selectNOldest, mapper) {
    public override fun execute(): SqlCursor = driver.executeQuery(98657912, """
    |SELECT * FROM statisticPayloadTable
    |ORDER BY timestampUnixMillis ASC
    |LIMIT ?
    """.trimMargin(), 1) {
      bindLong(1, n)
    }

    public override fun toString(): String = "StatisticPayload.sq:selectNOldest"
  }
}
